import { useState, useCallback, useMemo } from 'react';
import {
  Stack,
  Group,
  Title,
  Text,
  Button,
  SimpleGrid,
  Center,
  Loader,
  Alert,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useNavigate } from '@tanstack/react-router';
import { IconAlertCircle, IconPlus } from '@tabler/icons-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { hasAnyAppAccess, getAppLimit } from '@lib2/model/customer/QuotaFeaturesAppUtil';
import { useApps } from '../useApps';
import { useCreateApp, useDeleteApp, useToggleApp } from '../useAppMutations';
import { useAppContentListlists } from '@/features/apps-content/useAppContentListlists';
import { AppUtils } from '../AppUtils';
import { AppCard } from './AppCard';
import { CreateAppDialog } from './CreateAppDialog';
import type { AppCfgsDto } from '@/lib/types/App';

export function AppsPage() {
  const { selectedCustomer } = useAuth();
  const navigate = useNavigate();
  const { apps, isLoading, isError, error } = useApps();
  const { contentGroups } = useAppContentListlists();
  const createMutation = useCreateApp();
  const deleteMutation = useDeleteApp();
  const toggleMutation = useToggleApp();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Quota checks
  const quotaApp = selectedCustomer?.cfg.quota.features.app;
  const hasAccess = !!quotaApp && hasAnyAppAccess(quotaApp);
  const appLimit = getAppLimit(quotaApp);
  const isAtLimit = apps.length >= appLimit;

  // Build content group title lookup
  const contentGroupTitleMap = useMemo(() => {
    const map = new Map<number, string>();
    contentGroups.forEach((cg) => {
      map.set(cg.llr.llId, cg.llr.title);
    });
    return map;
  }, [contentGroups]);

  // Build card data
  const cards = useMemo(() => {
    return apps.map((appFiles) => {
      const rootLlId = appFiles.appCfgDto.appCfg.core.rootLlId;
      const groupTitle = contentGroupTitleMap.get(rootLlId) ?? 'Unknown Group';
      return {
        card: AppUtils.buildCardData(appFiles, groupTitle),
        appDto: appFiles.appCfgDto,
      };
    });
  }, [apps, contentGroupTitleMap]);

  const handleCreate = useCallback(
    (name: string, rootLlId: number, defaultPlayerId: number) => {
      const req = AppUtils.buildCreateReq(name, rootLlId, defaultPlayerId);
      createMutation.mutate(req, {
        onSuccess: (newApp) => {
          setCreateDialogOpen(false);
          notifications.show({
            title: 'App Created',
            message: `"${name}" has been created.`,
            color: 'green',
          });
          const displayId = AppUtils.getDisplayId(newApp.appCfgDto.appCfg.core.appCfgId);
          navigate({ to: '/apps/$id', params: { id: displayId } });
        },
        onError: (err) => {
          notifications.show({
            title: 'Error',
            message: err.message,
            color: 'red',
          });
        },
      });
    },
    [createMutation, navigate],
  );

  const handleToggle = useCallback(
    (appCfgId: number, currentApp: AppCfgsDto) => {
      toggleMutation.mutate(
        { appCfgId, currentApp },
        {
          onSuccess: () => {
            notifications.show({
              title: 'Updated',
              message: 'App status updated.',
              color: 'green',
            });
          },
          onError: (err) => {
            notifications.show({
              title: 'Error',
              message: err.message,
              color: 'red',
            });
          },
        },
      );
    },
    [toggleMutation],
  );

  const handleDelete = useCallback(
    (appCfgId: number, name: string) => {
      modals.openConfirmModal({
        title: 'Delete App',
        children: (
          <Text size="sm">
            Are you sure you want to delete &ldquo;{name}&rdquo;? This action cannot be
            undone.
          </Text>
        ),
        labels: { confirm: 'Delete', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          deleteMutation.mutate(appCfgId, {
            onSuccess: () => {
              notifications.show({
                title: 'Deleted',
                message: `"${name}" has been deleted.`,
                color: 'green',
              });
            },
            onError: (err) => {
              notifications.show({
                title: 'Error',
                message: err.message,
                color: 'red',
              });
            },
          });
        },
      });
    },
    [deleteMutation],
  );

  // Page-level quota guard
  if (!hasAccess) {
    return (
      <Stack>
        <Title order={2}>Apps</Title>
        <Alert icon={<IconAlertCircle size={16} />} color="orange">
          Your current plan does not include access to Apps. Contact support to upgrade.
        </Alert>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="md" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Stack>
        <Title order={2}>Apps</Title>
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          Failed to load apps: {error?.message ?? 'Unknown error'}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Apps</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setCreateDialogOpen(true)}
          disabled={isAtLimit}
        >
          {isAtLimit ? `App limit reached (${appLimit})` : 'Create App'}
        </Button>
      </Group>

      {cards.length === 0 ? (
        <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
          No apps yet. Create your first app to get started.
        </Alert>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {cards.map(({ card, appDto }) => (
            <AppCard
              key={card.appCfgId}
              card={card}
              appDto={appDto}
              onToggle={handleToggle}
              onDelete={handleDelete}
            />
          ))}
        </SimpleGrid>
      )}

      <CreateAppDialog
        opened={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreate}
        isPending={createMutation.isPending}
        contentGroups={contentGroups}
      />
    </Stack>
  );
}
